import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCamera,
  FaCheck,
  FaExclamationTriangle,
  FaRedo,
  FaSpinner,
  FaTimes,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { listingPhotoService } from '../../../../services/listingPhotoService';
import { LISTING_SHOTS } from '../../../../utils/partListingShots';
import ShelfListingStepper from '../components/ShelfListingStepper';

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function getClientCaptureMeta() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ capturedAt: new Date().toISOString() });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          capturedAt: new Date().toISOString(),
        });
      },
      () => resolve({ capturedAt: new Date().toISOString() }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  });
}

function ExitBlockedModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/80 p-4">
      <div className="bg-white rounded-xl max-w-sm w-full p-6 text-center">
        <FaExclamationTriangle className="text-amber-500 text-3xl mx-auto mb-3" />
        <p className="text-slate-800 font-semibold">Complete photo sequence to list this part.</p>
        <p className="text-sm text-slate-500 mt-2">
          All mandatory shots are required before you can create a listing.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm"
        >
          Continue photographing
        </button>
      </div>
    </div>
  );
}

function ConfirmGateModal({ prompt, onConfirm, onRetake }) {
  if (!prompt) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/80 p-4">
      <div className="bg-white rounded-xl max-w-sm w-full p-6">
        <FaExclamationTriangle className="text-amber-500 text-2xl mb-3" />
        <p className="text-slate-800 text-sm">{prompt.message}</p>
        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={onRetake}
            className="flex-1 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium"
          >
            Retake
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 py-2.5 rounded-lg bg-amber-600 text-white text-sm font-semibold"
          >
            Confirm & continue
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PartListingCameraWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const resumeState = location.state || {};
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const [sessionId] = useState(() => resumeState.sessionId || crypto.randomUUID());
  const [phase, setPhase] = useState(resumeState.captureShot4 ? 'capture' : 'capture');
  const [shots, setShots] = useState(resumeState.shots || []);
  const [currentIndex, setCurrentIndex] = useState(
    resumeState.captureShot4 ? LISTING_SHOTS.findIndex((s) => s.id === 'functional') : 0
  );
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [exitBlocked, setExitBlocked] = useState(false);
  const [gatePrompt, setGatePrompt] = useState(null);
  const [pendingCapture, setPendingCapture] = useState(null);

  const activeShotList = useMemo(() => {
    const hasFunctional = shots.some((s) => s.shotId === 'functional');
    if (resumeState.captureShot4 || hasFunctional) return LISTING_SHOTS;
    return LISTING_SHOTS.filter((s) => !s.gradeCOnly);
  }, [shots, resumeState.captureShot4]);

  const currentShot = activeShotList[currentIndex] || activeShotList[0];
  const totalShots = activeShotList.length;
  const minRequired = resumeState.captureShot4 || shots.some((s) => s.shotId === 'functional') ? 4 : 3;
  const completedCount = shots.length;
  const canProceedToListing = completedCount >= 3;

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch {
      setCameraError('Camera unavailable — use the upload button to capture from your device.');
      setCameraReady(false);
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  const captureFromVideo = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
    return dataUrl;
  };

  const verifyCapture = async (dataUrl, { confirmSimilarPhoto = false, confirmStalePhoto = false } = {}) => {
    const clientCaptureMeta = await getClientCaptureMeta();
    return listingPhotoService.verify({
      content: dataUrl,
      fileName: `listing-${currentShot?.id || 'shot'}.jpg`,
      mimeType: 'image/jpeg',
      shotType: currentShot?.id,
      sessionId,
      confirmSimilarPhoto,
      confirmStalePhoto,
      clientCaptureMeta,
    });
  };

  const acceptVerifiedShot = (result, previewUrl) => {
    const entry = {
      shotId: currentShot.id,
      shotLabel: currentShot.label,
      imageUrl: result.photo.imageUrl,
      photoHashId: result.photo.photoHashId,
      previewUrl,
      manualReview: result.photo.manualReview,
    };

    setShots((prev) => {
      const without = prev.filter((s) => s.shotId !== currentShot.id);
      return [...without, entry];
    });

    const nextIndex = currentIndex + 1;

    if (nextIndex >= activeShotList.length) {
      setPhase('review');
      stopCamera();
      return;
    }

    setCurrentIndex(nextIndex);
  };

  const processCapture = async (dataUrl, confirmFlags = {}) => {
    setProcessing(true);
    try {
      const result = await verifyCapture(dataUrl, confirmFlags);

      if (!result.accepted) {
        if (result.exif?.outcome === 'reject') {
          toast.error(result.exif.message);
          return;
        }
        if (result.hash?.outcome === 'block') {
          toast.error(result.hash.message);
          return;
        }
        if (result.requiresConfirmation) {
          setPendingCapture(dataUrl);
          setGatePrompt({
            confirmType: result.confirmType,
            message: result.hash?.message || result.exif?.message,
          });
          return;
        }
        toast.error(result.exif?.message || result.hash?.message || 'Photo not accepted');
        return;
      }

      acceptVerifiedShot(result, dataUrl);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Photo verification failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleShutter = async () => {
    if (processing || !currentShot) return;
    const dataUrl = await captureFromVideo();
    if (!dataUrl) {
      fileInputRef.current?.click();
      return;
    }
    await processCapture(dataUrl);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || processing) return;
    try {
      const { prepareStoredUploadFile } = await import('../../../auth/partnerUploadFile');
      const { file: prepared } = await prepareStoredUploadFile(file, { cameraOnly: true });
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('read'));
        reader.readAsDataURL(prepared);
      });
      await processCapture(dataUrl);
    } catch {
      toast.error('Could not process photo');
    }
  };

  const handleGateConfirm = async () => {
    if (!pendingCapture || !gatePrompt) return;
    const flags =
      gatePrompt.confirmType === 'similar_photo'
        ? { confirmSimilarPhoto: true }
        : { confirmStalePhoto: true };
    setGatePrompt(null);
    await processCapture(pendingCapture, flags);
    setPendingCapture(null);
  };

  const handleRetakeShot = (shotId) => {
    const idx = activeShotList.findIndex((s) => s.id === shotId);
    setShots((prev) => prev.filter((s) => s.shotId !== shotId));
    setCurrentIndex(idx >= 0 ? idx : 0);
    setPhase('capture');
    startCamera();
  };

  const hasIncompleteSequence = () => {
    if (shots.length === 0) return false;
    if (resumeState.captureShot4) return !shots.some((s) => s.shotId === 'functional');
    return shots.length < 3;
  };

  const handleBack = () => {
    if (phase === 'review' && canProceedToListing) {
      navigate('/vendor/dashboard/parts/add/details', {
        state: { shots, sessionId },
      });
      return;
    }
    if (hasIncompleteSequence()) {
      setExitBlocked(true);
      return;
    }
    navigate('/vendor/dashboard/products');
  };

  const handleProceedToDetails = () => {
    if (!canProceedToListing) {
      toast.error('Complete at least 3 photos first');
      return;
    }
    navigate('/vendor/dashboard/parts/add/details', {
      state: { shots, sessionId },
    });
  };

  if (phase === 'review') {
    const orderedShots = activeShotList;
    return (
      <div className="fixed inset-0 z-50 bg-slate-100 flex flex-col">
        <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button type="button" onClick={handleBack} className="text-slate-600 p-2">
            <FaArrowLeft />
          </button>
          <h1 className="font-bold text-slate-900">Review photos</h1>
          <span className="text-xs text-emerald-700 font-semibold">{shots.length} shots</span>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-lg mx-auto mb-4">
            <ShelfListingStepper current={1} />
          </div>
          <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto">
            {orderedShots.map((def) => {
              const captured = shots.find((s) => s.shotId === def.id);
              return (
                <div key={def.id} className="rounded-xl overflow-hidden bg-white border border-slate-200">
                  {captured ? (
                    <img
                      src={captured.previewUrl || captured.imageUrl}
                      alt=""
                      className="w-full aspect-square object-cover"
                    />
                  ) : (
                    <div className="w-full aspect-square bg-slate-200 flex items-center justify-center text-slate-400 text-xs">
                      {def.gradeCOnly ? 'Grade C only' : 'Missing'}
                    </div>
                  )}
                  <div className="p-2">
                    <p className="text-xs font-semibold text-slate-800">{def.label}</p>
                    <button
                      type="button"
                      onClick={() => handleRetakeShot(def.id)}
                      className="mt-1 text-[10px] text-emerald-700 font-semibold flex items-center gap-1"
                    >
                      <FaRedo /> Retake
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {!shots.some((s) => s.shotId === 'functional') && (
            <button
              type="button"
              onClick={() => {
                setCurrentIndex(LISTING_SHOTS.findIndex((s) => s.id === 'functional'));
                setPhase('capture');
                startCamera();
              }}
              className="mt-4 w-full max-w-lg mx-auto block py-2.5 rounded-lg border border-slate-300 text-sm font-medium text-slate-700"
            >
              Add Grade C functional surfaces photo (optional until Grade C selected)
            </button>
          )}
        </div>
        <div className="p-4 bg-white border-t border-slate-200">
          <button
            type="button"
            onClick={handleProceedToDetails}
            disabled={!canProceedToListing}
            className="w-full py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <FaCheck /> Continue to part details
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <ExitBlockedModal open={exitBlocked} onClose={() => setExitBlocked(false)} />
      <ConfirmGateModal
        prompt={gatePrompt}
        onConfirm={handleGateConfirm}
        onRetake={() => {
          setGatePrompt(null);
          setPendingCapture(null);
        }}
      />

      <div className="absolute top-0 left-0 right-0 z-10 p-4 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent">
        <button type="button" onClick={handleBack} className="text-white p-2 rounded-full bg-black/40">
          <FaTimes />
        </button>
        <div className="text-center text-white">
          <p className="text-xs font-medium opacity-80">
            Shot {Math.min(currentIndex + 1, totalShots)} of {totalShots}
          </p>
          <p className="text-sm font-bold">{currentShot?.label}</p>
        </div>
        <div className="w-10" />
      </div>

      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        {cameraReady ? (
          <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted />
        ) : (
          <div className="text-white text-center p-6">
            <FaSpinner className="animate-spin text-3xl mx-auto mb-3" />
            <p className="text-sm">{cameraError || 'Starting camera…'}</p>
          </div>
        )}

        <div className="absolute inset-0 pointer-events-none flex flex-col justify-end pb-32 px-6">
          <div className="bg-black/55 backdrop-blur-sm rounded-xl p-4 text-white text-center border border-white/10">
            <p className="text-sm font-semibold">{currentShot?.guidance}</p>
            <p className="text-[10px] text-white/70 mt-1">{currentShot?.shortHint}</p>
          </div>
        </div>

        <div className="absolute inset-8 border-2 border-white/30 rounded-2xl pointer-events-none" />
      </div>

      <div className="bg-black px-6 py-5 flex items-center justify-center gap-8">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="text-white/80 text-xs font-medium"
          disabled={processing}
        >
          Upload
        </button>
        <button
          type="button"
          onClick={handleShutter}
          disabled={processing}
          className="w-16 h-16 rounded-full bg-white flex items-center justify-center ring-4 ring-white/30 disabled:opacity-50"
        >
          {processing ? (
            <FaSpinner className="animate-spin text-slate-800 text-xl" />
          ) : (
            <FaCamera className="text-slate-800 text-xl" />
          )}
        </button>
        <span className="text-white/60 text-xs w-12 text-center">{completedCount}/{minRequired}</span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={handleFileSelected}
      />
    </div>
  );
}
